import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, MailOpen, Package, Send, User, Store } from "lucide-react";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [replies, setReplies] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const load = () => api.get("/admin/messages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.put(`/admin/messages/${id}/read`); load(); } catch (e) {}
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette conversation ?")) return;
    await api.delete(`/admin/messages/${id}`);
    toast.success("Conversation supprimée");
    load();
  };
  const sendReply = async (id) => {
    const text = (replies[id] || "").trim();
    if (!text) return;
    setSendingId(id);
    try {
      await api.post(`/admin/messages/${id}/reply`, { message: text });
      setReplies((r) => ({ ...r, [id]: "" }));
      toast.success("Réponse envoyée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSendingId(null);
    }
  };

  const filtered = items.filter((m) => {
    if (filter === "unread") return !m.read;
    if (filter === "reservation") return m.type === "reservation";
    if (filter === "contact") return m.type === "contact";
    return true;
  });
  const unreadCount = items.filter((m) => !m.read).length;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="admin-messages-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-3">Boîte de réception</div>
          <h1 className="font-display text-5xl">Conversations</h1>
          <p className="text-[#a3a8a4] mt-2">{unreadCount} non lu{unreadCount > 1 ? "s" : ""} sur {items.length}.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-8 mb-6 fade-up-delay-1">
        {[
          { id: "all", label: "Tous" },
          { id: "unread", label: "Non lus" },
          { id: "reservation", label: "Réservations" },
          { id: "contact", label: "Messages" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`} className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${filter === f.id ? "border-[#d4af37] text-[#d4af37]" : "border-[rgba(253,251,247,0.1)] text-[#a3a8a4] hover:text-[#fdfbf7]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-4 fade-up-delay-2">
        {filtered.length === 0 && <div className="border border-[rgba(253,251,247,0.08)] p-10 text-center text-[#a3a8a4]">Aucun message.</div>}
        {filtered.map((m) => (
          <div key={m.id} className={`border ${m.read ? "border-[rgba(253,251,247,0.08)]" : "border-[#d4af37]/40 bg-[#1a241b]/20"} p-6`} data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="label-eyebrow">{m.type === "reservation" ? "Réservation" : "Contact"}</span>
                  {m.product_name && (
                    <span className="text-xs text-[#a3a8a4] inline-flex items-center gap-1"><Package size={12}/> {m.product_name}</span>
                  )}
                  {!m.read && <span className="text-xs gold-text">● Nouveau</span>}
                </div>
                <div className="font-display text-xl">{m.user_firstName}</div>
                <a href={`mailto:${m.user_email}?subject=Votre demande Noctalis`} className="text-xs text-[#a3a8a4] hover:gold-text">{m.user_email}</a>
              </div>
              <div className="flex flex-col gap-2">
                {!m.read && (
                  <button onClick={() => markRead(m.id)} className="border border-[rgba(253,251,247,0.1)] hover:border-[#d4af37] inline-flex items-center gap-1 text-xs px-3 py-1.5" data-testid={`mark-read-${m.id}`}>
                    <MailOpen size={14}/> Marquer lu
                  </button>
                )}
                <button onClick={() => remove(m.id)} className="border border-[rgba(253,251,247,0.1)] hover:border-red-500 hover:text-red-400 inline-flex items-center gap-1 text-xs px-3 py-1.5" data-testid={`delete-message-${m.id}`}>
                  <Trash2 size={14}/> Supprimer
                </button>
              </div>
            </div>

            {/* Thread */}
            <div className="space-y-3 border-l-2 border-[rgba(253,251,247,0.08)] pl-4 my-4">
              <Bubble from="client" name={m.user_firstName} text={m.message} at={m.created_at}/>
              {(m.replies || []).map((r) => (
                <Bubble key={r.id} from={r.from} name={r.from === "admin" ? (r.admin_firstName || "Vous") : m.user_firstName} text={r.message} at={r.at}/>
              ))}
            </div>

            {/* Reply box */}
            <div className="space-y-2">
              <textarea
                value={replies[m.id] || ""}
                onChange={(e) => setReplies((r) => ({ ...r, [m.id]: e.target.value }))}
                rows={2}
                placeholder="Répondre au client..."
                className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"
                data-testid={`reply-input-${m.id}`}
              />
              <button
                onClick={() => sendReply(m.id)}
                disabled={sendingId === m.id || !(replies[m.id] || "").trim()}
                className="bg-[#d4af37] text-black px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#e6c27a] inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`reply-send-${m.id}`}
              >
                <Send size={14}/> {sendingId === m.id ? "Envoi..." : "Répondre"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bubble({ from, name, text, at }) {
  const isAdmin = from === "admin";
  return (
    <div className={`flex gap-3 ${isAdmin ? "justify-end" : "justify-start"}`}>
      {!isAdmin && (
        <div className="w-8 h-8 rounded-full bg-[#1a241b] flex items-center justify-center flex-shrink-0"><User size={14}/></div>
      )}
      <div className={`max-w-[75%] px-4 py-3 ${isAdmin ? "bg-[#d4af37] text-black" : "bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)]"}`}>
        <div className={`text-[10px] uppercase tracking-widest mb-1 ${isAdmin ? "text-black/60" : "text-[#a3a8a4]"}`}>
          {isAdmin ? "Vous" : name} · {new Date(at).toLocaleString("fr-FR")}
        </div>
        <div className="text-sm whitespace-pre-wrap">{text}</div>
      </div>
      {isAdmin && (
        <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0"><Store size={14}/></div>
      )}
    </div>
  );
}
